import { useEffect, useState } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { formatNumber } from "utils/utils";
import { formatTipDate } from "../logic/tips.date.logic";
import {
  hasApprovedTimeReports,
  hasPersistedDistribution,
  isPaidOutTip,
  isReadyToPayoutTip,
} from "../logic/tips.v2.logic";
import {
  RowActionButton,
  RowActionSlot,
  RowCheckbox,
  RowDate,
  RowTextGroup,
  TipAmountInput,
  TipRow,
  TipRowCard,
  TipRowValue,
  TipWorkersPanel,
} from "../tips.styled";
import { TipsAmountResetWarningModal } from "./tips.amountResetWarningModal.component";
import { TipRowManagementAction } from "./tips.rowManagementAction.component";
import { TipRowPaymentAction } from "./tips.rowPaymentAction.component";
import { tipAmountInputSelector } from "./tips.rows.constants";
import {
  getDisplayRemainingAmount,
  getTipRowPaymentStatus,
  isManualPaidOutTip,
} from "./tips.rows.logic";
import type { TipRowItemProps } from "./tips.rows.types";
import { TipWorkersPanelContent } from "./tips.workersPanel.component";

export const TipRowItem = (props: TipRowItemProps): JSX.Element => {
  const { i18n, t } = useTranslation();
  const [amountInput, setAmountInput] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [pendingAmount, setPendingAmount] = useState<number | null>(null);

  useEffect(() => {
    setAmountInput(props.tip.amount === 0 ? "" : props.tip.amount.toString());
  }, [props.tip.amount]);

  const handleAmountChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const nextValue = event.target.value;
    if (!/^\d*$/.test(nextValue)) return;
    setAmountInput(nextValue);
  };

  const handleAmountBlur = (): void => {
    const amount = amountInput.trim().length === 0 ? 0 : Number(amountInput);
    if (!Number.isFinite(amount)) return;
    if (amount === props.tip.amount) return;
    if (tipHasPersistedDistribution) {
      setPendingAmount(amount);
      return;
    }
    props.onUpdateAmount(props.tip.companyTipId, amount);
  };

  const handleAmountKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key !== "Tab") return;

    const inputs = Array.from(
      document.querySelectorAll<HTMLInputElement>(tipAmountInputSelector),
    ).filter((input) => !input.disabled);
    const currentIndex = inputs.indexOf(event.currentTarget);
    const nextIndex = currentIndex + (event.shiftKey ? -1 : 1);
    const nextInput = inputs[nextIndex];

    if (!nextInput) return;

    event.preventDefault();
    nextInput.focus();
  };

  const currentAmount =
    amountInput.trim().length === 0 ? 0 : Number(amountInput);
  const rowPaymentStatus = getTipRowPaymentStatus(props.tip);
  const isReadyForPayout = isReadyToPayoutTip(props.tip);
  const isPaidOut = isPaidOutTip(props.tip);
  const tipHasApprovedTimeReports = hasApprovedTimeReports(props.tip);
  const tipHasPersistedDistribution = hasPersistedDistribution(props.tip);
  const hasWorkers = props.tip.workers.length > 0;
  const displayRemainingAmount = getDisplayRemainingAmount({
    currentAmount,
    hasPersistedDistribution: tipHasPersistedDistribution,
    inputValue: amountInput,
    isReadyForPayout,
    remainingAmount: props.tip.remainingAmount,
    rowPaymentStatus,
  });
  const shouldShowManualPayoutRevert = isManualPaidOutTip(props.tip);
  const shouldShowDelete = rowPaymentStatus !== "swish";
  const closeAmountWarningModal = (): void => {
    setPendingAmount(null);
  };
  const resetAmountInput = (): void => {
    setAmountInput(props.tip.amount === 0 ? "" : props.tip.amount.toString());
  };

  return (
    <>
      <TipRowCard>
        <TipRow>
          <RowCheckbox
            type="checkbox"
            aria-label={t("Tips.SelectTip")}
            checked={props.isSelected}
            onChange={() => props.onToggleSelected(props.tip.companyTipId)}
          />
          <RowTextGroup>
            <RowDate>
              {formatTipDate({
                dateValue: props.tip.date,
                locale: i18n.language,
              })}
            </RowDate>
          </RowTextGroup>
          <TipRowValue>
            <span>{t("Tips.EarnedTips")}</span>
            <TipAmountInput
              aria-label={t("Tips.EarnedTips")}
              data-tip-amount-input="true"
              disabled={isPaidOut}
              inputMode="numeric"
              maxLength={7}
              onBlur={handleAmountBlur}
              onChange={handleAmountChange}
              onKeyDown={handleAmountKeyDown}
              placeholder={`0 ${t("Tips.Currency")}`}
              value={amountInput}
            />
          </TipRowValue>
          <TipRowValue>
            <span>{t("Tips.LeftToDistribute")}</span>
            <strong>
              {formatNumber(displayRemainingAmount)} {t("Tips.Currency")}
            </strong>
          </TipRowValue>
          <TipRowPaymentAction
            currentAmount={currentAmount}
            hasApprovedTimeReports={tipHasApprovedTimeReports}
            hasWorkers={hasWorkers}
            hasPersistedDistribution={tipHasPersistedDistribution}
            isReadyForPayout={isReadyForPayout}
            rowPaymentStatus={rowPaymentStatus}
            tip={props.tip}
            onDistribute={props.onDistribute}
            onOpenPayout={props.onOpenPayout}
          />
          <TipRowManagementAction
            shouldShowDelete={shouldShowDelete}
            shouldShowManualPayoutRevert={shouldShowManualPayoutRevert}
            tip={props.tip}
            onDelete={props.onDelete}
            onRevertManualPayout={props.onRevertManualPayout}
          />
          <RowActionSlot $column={7}>
            <RowActionButton
              type="button"
              aria-label={t("Tips.ExpandTip")}
              aria-expanded={isExpanded}
              onClick={() => setIsExpanded((currentValue) => !currentValue)}
            >
              <CustomIcon
                name={isExpanded ? "v2/up-chevron" : "v2/down-chevron"}
                size="22px"
                color={ColorV2.Blue1}
              />
            </RowActionButton>
          </RowActionSlot>
        </TipRow>
        {isExpanded && (
          <TipWorkersPanel>
            <TipWorkersPanelContent
              rowPaymentStatus={rowPaymentStatus}
              workers={props.tip.workers}
            />
          </TipWorkersPanel>
        )}
      </TipRowCard>
      <TipsAmountResetWarningModal
        isOpen={pendingAmount !== null}
        onCancel={() => {
          resetAmountInput();
          closeAmountWarningModal();
        }}
        onConfirm={() => {
          if (pendingAmount !== null) {
            props.onUpdateAmount(props.tip.companyTipId, pendingAmount);
          }
          closeAmountWarningModal();
        }}
      />
    </>
  );
};
